import AdUtils from '@shared/utils/ads';
import AnalyticsInstance, { AnalyticEventType } from '@shared/utils/analytics';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import Routes from '@shared/types/enums/routes';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PlayerState } from '@shared/types/enums/player';
import { mediaSelector, play } from '@shared/store/slices/media.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { setParams } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef } from 'react';
import {
  adsSelector,
  clearPauseAd,
  setImpressionsUrls,
  setPauseAd,
} from '@shared/store/slices/ads.slice';
import {
  filterFocus,
  popFocus,
  pushFocus,
  setFocus,
} from '@shared/store/slices/focus.slice';

interface Props {
  asset?: Event;
  togglePilot: (display: boolean) => void;
  keyHandler: (key: string) => void;
  useKeyboard: (props: {
    focus?: Focus | number | Focus[] | number[];
    shouldFocus?: boolean;
    keyDownHandler?: (key: string) => void;
    keyPressHandler?: (() => void) | null;
    keyUpHandler?: (key: string) => void;
  }) => void;
}

/**
 * Custom hook that manages pause ad display logic.
 * Fetches and displays pause ads when the player is paused, and clears the modal when playback resumes.
 *
 * @param {Event} [asset] - Optional event asset to display pause ad information for
 */
export default function usePauseAds({
  asset,
  keyHandler,
  togglePilot,
  useKeyboard,
}: Props) {
  const { pauseAd } = useAppSelector(adsSelector);
  const { state } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const dispatch = useAppDispatch();
  const keyboardHandlerRef = useRef(keyHandler);
  keyboardHandlerRef.current = keyHandler;

  // Callbacks

  const triggerPauseAd = useCallback(
    (response: {
      url?: string;
      selectionTrackers?: string[];
      impressionTrackers?: string[];
      adId?: string;
    }) => {
      // if(response.adId) {
      //   AnalyticsInstance.getOmnitrackManager()?.getPauseAdSession()?.setAdId(response.adId);
      // } TBD: This field is not approved for development
      AnalyticsInstance.getOmnitrackManager()
        ?.getPauseAdSession()
        ?.setPauseAdResponseAt(new Date().toISOString());
      if (!asset) return;
      if (response.url) {
        if (response.impressionTrackers) {
          response.impressionTrackers.forEach((item: string) => {
            AdUtils.sendImageImpression(item);
          });
          store.dispatch(setImpressionsUrls(response.impressionTrackers));
        }
        setPauseAd({ asset, background: response.url });
        dispatch(setPauseAd({ asset, background: response.url }));
        togglePilot(false);
      } else {
        AnalyticsInstance.getOmnitrackManager()
          ?.getPauseAdSession()
          ?.setWasAdShown(false);
        AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
      }
    },
    [asset]
  );

  // Effect hooks

  // Contruction & destruction logic
  useEffect(() => {
    return () => {
      const { router } = store.getState();
      if (
        !(
          router.route.stack.includes(Pages.PLAYER) ||
          router.route.stack.includes(Pages.ZAPPER)
        )
      ) {
        dispatch(clearPauseAd());
      }
    };
  }, []);

  // Focus management
  useEffect(() => {
    if (pauseAd) {
      // Delay is required as Player/Zapper initial setFocus can replace this value
      setTimeout(() => {
        dispatch(pushFocus(Focus.PAUSE_ADS));
      });
    } else {
      const { router, modal } = store.getState();
      if (!modal?.current) {
        if (router.route.Page === Pages.PLAYER) {
          dispatch(setFocus(Focus.PLAYER));
        } else if (router.route.Page === Pages.ZAPPER) {
          dispatch(setFocus(Focus.ZAPPER));
        }
      }
    }
    return () => {
      const { focus } = store.getState();
      if (focus.focus === Focus.PAUSE_ADS) {
        dispatch(popFocus());
      } else {
        dispatch(filterFocus(Focus.PAUSE_ADS));
      }
    };
  }, [pauseAd]);

  // Modal management
  useEffect(() => {
    if (pauseAd && modal === Modals.PLAYER_LANGUAGE) {
      dispatch(clearPauseAd());
      dispatch(play());
    }
  }, [pauseAd, modal]);

  // Player state change behaviour
  useEffect(() => {
    // On pause behaviour
    if (state === PlayerState.PAUSE) {
      const source = AdUtils.getSource('pause', false, asset?.channel, true);
      if (source) {
        AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_REQUEST);
        AdUtils.fetchAdImage(
          source,
          {
            clicks: [],
            impressions: [],
          },
          ''
        ).then((response) => {
          if (response.url) {
            triggerPauseAd(response);
          } else {
            const planSource = AdUtils.getSource(
              'pause',
              false,
              asset?.channel,
              true,
              false,
              true
            );
            AdUtils.fetchAdImage(
              planSource,
              {
                clicks: [],
                impressions: [],
              },
              ''
            ).then(triggerPauseAd);
          }
        });
      }
    }
  }, [state]);

  // Keyboard hook

  useKeyboard({
    focus: Focus.PAUSE_ADS,
    keyDownHandler: () => {
      return null;
    },
    keyUpHandler: (key) => {
      let shouldClear = false;
      let shouldPlay = false;
      switch (key) {
        case 'SELECT':
        case 'BACK':
          shouldClear = true;
          shouldPlay = true;
          togglePilot(true);
          break;
        case 'CHANNEL_UP':
        case 'CHANNEL_DOWN':
        case 'REWIND':
        case 'LEFT':
        case 'FASTFORWARD':
        case 'RIGHT':
        case 'UP':
        case 'DOWN':
          if (keyboardHandlerRef.current) {
            keyboardHandlerRef.current(key);
          }
          shouldClear = true;
      }
      setTimeout(() => {
        shouldClear && dispatch(clearPauseAd());
        shouldPlay && dispatch(play());
      });
    },
    keyPressHandler: () => {
      const { ads, floatingMenu } = store.getState();
      if (!floatingMenu.open) {
        const { router, media } = store.getState();
        const params = router.params.current as {
          content: { keepWatchingMark: number };
        };
        if (params?.content)
          params.content.keepWatchingMark = media.currentTime;
        const stackParams = {
          ...params,
        };
        dispatch(setParams(stackParams));
        dispatch(
          openFloatingMenu({
            asset: ads.pauseAd?.asset,
            position: Coordinates.PAUSE_ADS,
            route:
              router.route.Page === Pages.ZAPPER
                ? Routes.ZAPPER
                : Routes.PLAYER,
          })
        );
      }
    },
  });
}
